import classNames from 'classnames';
import { debounce } from 'lodash';
import { memo, useCallback, useState } from 'react';

import { SearchIcon, XIcon } from '@air/design-icons';
import { IconButton } from '@air/primitive-icon-button';
import { Input } from '@air/primitive-input';
import { Tooltip } from '@air/primitive-tooltip';

export type SimpleSearchBarProps = {
  name: string;
  onSearchTermChange: (search: string) => void;
  placeholder?: string;
  hideCloseButton?: boolean;
};

/**
 * @description A SearchBar component that does not use ComboBox
 */
export const SimpleSearchBar = memo(
  ({ onSearchTermChange, placeholder, name, hideCloseButton = false }: SimpleSearchBarProps) => {
    const [value, setValue] = useState('');
    // eslint complains about passing inline function, but only this version works with debounce
    // biome-ignore lint/style/noMagicNumbers lint/correctness/useExhaustiveDependencies: PLA-1724 — pre-existing debt, not introduced by the icon migration
    const debouncedOnSearchChange = useCallback(debounce(onSearchTermChange, 600), [onSearchTermChange]);

    const hasCloseButton = !!value && !hideCloseButton;

    return (
      <div className="relative">
        <SearchIcon className="absolute top-3 left-3 text-10 text-gray-9" size="sm" />
        <Input
          data-1p-ignore
          className={classNames('w-full pl-9', hasCloseButton ? 'pr-9' : '')}
          size="large"
          value={value}
          // biome-ignore lint/performance/noJsxPropsBind: PLA-1724 — pre-existing debt, not introduced by the icon migration
          onChange={(evt) => {
            setValue(evt.target.value);
            debouncedOnSearchChange(evt.target.value);
          }}
          placeholder={placeholder}
          name={name}
          data-testid="SIMPLE_SEARCH_BAR_INPUT"
        />
        {hasCloseButton && (
          <Tooltip label="Clear search">
            <IconButton
              adsIcon
              className="absolute top-3 right-3"
              appearance="ghost"
              color="gray"
              label="Clear search"
              icon={XIcon}
              // biome-ignore lint/performance/noJsxPropsBind: PLA-1724 — pre-existing debt, not introduced by the icon migration
              onClick={() => {
                setValue('');
                onSearchTermChange('');
              }}
              size="extra-small"
            />
          </Tooltip>
        )}
      </div>
    );
  },
);

SimpleSearchBar.displayName = 'SimpleSearchBar';
